// src/admin/AdminProductVariants.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

function AdminProductVariants() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct]   = useState(null);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [message, setMessage]   = useState(null);

  const [form, setForm] = useState({
    size: '',
    color: '',
    stock: 0,
  });

  const [editingId, setEditingId] = useState(null);

  /* =========================================================
     CHARGEMENT
  ========================================================= */
  const fetchProduct = async () => {
    try {
      const { data } = await axios.get(`https://madadline.com/api/products/${id}`);
      setProduct(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchVariants = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('https://madadline.com/api/variants', {
        params: { product_id: id },
      });
      setVariants(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setVariants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
    fetchVariants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* =========================================================
     HANDLERS
  ========================================================= */
  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const resetForm = () => {
    setForm({ size: '', color: '', stock: 0 });
    setEditingId(null);
    setMessage(null);
  };

  const startEdit = (v) => {
    setForm({ size: v.size, color: v.color, stock: v.stock });
    setEditingId(v.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* =========================================================
     AJOUTER / MODIFIER
  ========================================================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!form.size.trim() || !form.color.trim()) {
      setMessage({ type: 'error', text: 'Taille et couleur sont obligatoires.' });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const payload = {
        product_id: parseInt(id, 10),
        size:       form.size.trim(),
        color:      form.color.trim(),
        stock:      parseInt(form.stock, 10) || 0,
      };

      if (editingId) {
        await axios.put(
          `https://madadline.com/api/variants/${editingId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage({ type: 'success', text: '✓ Variante modifiée' });
      } else {
        await axios.post(
          'https://madadline.com/api/variants',
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage({ type: 'success', text: '✓ Variante ajoutée' });
      }

      await fetchVariants();
      resetForm();
      setTimeout(() => setMessage(null), 2500);
    } catch (err) {
      console.error(err);
      const errs = err.response?.data;
      let errText = "Erreur lors de l'ajout.";
      if (errs) {
        if (errs.message) errText = errs.message;
        else {
          const firstKey = Object.keys(errs)[0];
          if (firstKey) errText = errs[firstKey][0];
        }
      }
      setMessage({ type: 'error', text: errText });
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     SUPPRIMER
  ========================================================= */
  const handleDelete = async (variantId) => {
    if (!window.confirm('Supprimer cette variante ?')) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`https://madadline.com/api/variants/${variantId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchVariants();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la suppression.');
    }
  };

  /* =========================================================
     COULEURS CSS
  ========================================================= */
  const getColorHex = (color) => {
    const map = {
      rouge: '#DC2626', noir: '#1A1A1A', blanc: '#FFFFFF',
      bleu: '#3B82F6', vert: '#10B981', jaune: '#F59E0B',
      rose: '#EC4899', violet: '#8B5CF6', marron: '#78350F',
      gris: '#6B7280', beige: '#F5F5DC', nude: '#E5C9B8',
      crème: '#FFFFFF', creme: '#FFFFFF', camel: '#C19A6B',
    };
    return map[(color || '').toLowerCase()] || '#C21E56';
  };

  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <div className="admin-variants">
      <header className="admin-page-head">
        <div>
          <button
            onClick={() => navigate('/admin/produits')}
            className="admin-btn-sm"
            style={{ marginBottom: '8px' }}
          >
            ← Retour aux produits
          </button>
          <h1>Variantes {product ? `· ${product.nom}` : ''}</h1>
          <p>{variants.length} variante{variants.length > 1 ? 's' : ''}</p>
        </div>
      </header>

      {message && (
        <div className={`admin-alert admin-alert--${message.type === 'success' ? 'success' : 'error'}`}>
          {message.text}
        </div>
      )}

      {/* FORMULAIRE */}
      <div className="admin-card">
        <div className="admin-card__head">
          <h2>{editingId ? 'Modifier la variante' : 'Ajouter une variante'}</h2>
          {editingId && (
            <button className="admin-btn-sm" onClick={resetForm}>Annuler</button>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="admin-form__grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="admin-form__group">
              <label>Taille *</label>
              <input
                type="text"
                name="size"
                value={form.size}
                onChange={handleChange}
                placeholder="S, M, L, XL..."
                required
              />
            </div>

            <div className="admin-form__group">
              <label>Couleur *</label>
              <input
                type="text"
                name="color"
                value={form.color}
                onChange={handleChange}
                placeholder="Noir, Beige, Rose..."
                required
              />
            </div>

            <div className="admin-form__group">
              <label>Stock *</label>
              <input
                type="number"
                name="stock"
                value={form.stock}
                onChange={handleChange}
                min="0"
                required
              />
            </div>
          </div>

          <div className="admin-form__actions">
            <button type="submit" className="admin-btn admin-btn--primary" disabled={saving}>
              {saving ? 'Enregistrement…' : editingId ? 'Mettre à jour' : '+ Ajouter la variante'}
            </button>
          </div>
        </form>
      </div>

      {/* LISTE */}
      {loading ? (
        <div className="admin-loading"><div className="spinner" /></div>
      ) : variants.length === 0 ? (
        <div className="admin-empty">
          <p>Aucune variante pour ce produit.</p>
        </div>
      ) : (
        <div className="admin-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Taille</th>
                <th>Couleur</th>
                <th>Stock</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {variants.map(v => (
                <tr key={v.id}>
                  <td><strong>{v.size || '—'}</strong></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        style={{
                          width: '16px', height: '16px', borderRadius: '50%',
                          background: getColorHex(v.color),
                          border: '1px solid rgba(0,0,0,0.1)',
                          flexShrink: 0,
                        }}
                      />
                      <span>{v.color || '—'}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`admin-badge admin-badge--${v.stock > 5 ? 'success' : v.stock > 0 ? 'warning' : 'danger'}`}>
                      {v.stock}
                    </span>
                  </td>
                  <td>
                    <div className="admin-table__actions">
                      <button onClick={() => startEdit(v)} className="admin-btn-sm admin-btn-sm--edit">
                        Modifier
                      </button>
                      <button onClick={() => handleDelete(v.id)} className="admin-btn-sm admin-btn-sm--danger">
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminProductVariants;